// @flow
import React from 'react';
import { isEmail } from 'validator';
import {
  get, includes, without,
  uniq, concat, endsWith, isEqual, trim,
} from 'lodash';
import { isValidBrPhone, isValidCpf, isValidBrDate } from '@helpers/validator/rules/testers';
import {
  Icon, Button, Input,
  Modal, InputMask,
} from '@elements';
import type { FormElementData } from '@types';
import { handleSubmitEvent } from '@helpers/helpers';


type State = {
  errors: Array<string>,
  formData: {
    name: string,
    birthdate: string,
    cpf: string,
    mobilePhone: string,
    email: string,
    recommendedBy: string,
    insurancePlan: string,
  }
};

type Props = {
  setModal: Function,
  onCreatePatient: Function,
  searchValue: string,
  isSavingPatient: boolean,
  previousModal: any,
  clinic: any,
  _: Function,
};

// Empty is fine (the e-mail is optional); a keyboard suggestion's trailing space isn't an error
const isValidEmail = (value: any) => !trim(value) || isEmail(trim(value));

export default class NewPatientForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      errors: [],
      formData: {
        name: this.props.searchValue || '',
        birthdate: '',
        cpf: '',
        mobilePhone: '',
        email: '',
        recommendedBy: '',
        insurancePlan: '',
      },
    };
  }

  componentDidMount() {
    handleSubmitEvent('#newPatientFormOnAppoitment', this.onCreatePatient);
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  render() {
    const { errors, formData } = this.state;
    const { isSavingPatient, clinic, _ } = this.props;

    const mobilePhone = get(formData, 'mobilePhone', '');

    return (
      <Modal id="patientModal" className="secondaryModal">
        <div className="modalHeader actionHeader">
          <Button className="backButton" onClick={this.handleClose}>
            <Icon iconName="arrow-left" className="icon" />
          </Button>
          <span>{_('label/patient_form/title')}</span>
        </div>
        <div className="modalGrid">
          <div className="modalContainer">
            <div className="modalContent">
              <div id="newPatientFormOnAppoitment" className="form">
                <Input
                  type="text"
                  name="name"
                  label={_('label/patient_form/name', null, s => `${s}:`)}
                  required
                  value={get(formData, 'name', '')}
                  error={includes(errors, 'name') ? _('error/name', 'global') : ''}
                  onChange={this.handleFormChange}
                  autoComplete="off"
                  autofocus
                  // In the apps too: you just tapped Cadastrar novo paciente
                  mobileAutofocus
                />

                <InputMask
                  type="phone"
                  name="mobilePhone"
                  label={_('label/patient_form/mobile_phone', null, s => `${s}:`)}
                  value={mobilePhone}
                  onChange={this.handleFormChange}
                  error={includes(errors, 'mobilePhone') ? _('error/invalid_phone', 'global') : ''}
                  languageOverride={clinic.international}
                />

                <Input
                  type="text"
                  name="email"
                  label={_('label/patient_form/email', null, s => `${s}:`)}
                  value={get(formData, 'email', '')}
                  error={includes(errors, 'email') ? _('error/invalid_email', 'global') : ''}
                  onChange={this.handleFormChange}
                  onBlur={this.handleEmailBlur}
                />

                <Input
                  type="text"
                  name="recommendedBy"
                  label={_('label/patient_form/recommended_by', null, s => `${s}:`)}
                  value={get(formData, 'recommendedBy', '')}
                  onChange={this.handleFormChange}
                />

                <Input
                  type="text"
                  name="insurance_company"
                  label={_('label/patient_form/insurance_plan', null, s => `${s}:`)}
                  value={get(formData, 'insurance_company', '')}
                  onChange={this.handleFormChange}
                />

                <InputMask
                  type="date"
                  name="birthdate"
                  label={_('label/patient_form/birthdate', null, s => `${s}:`)}
                  value={get(formData, 'birthdate', '')}
                  onChange={this.handleFormChange}
                  error={includes(errors, 'birthdate') ? _('error/date', 'global') : ''}
                />

                <InputMask
                  type="cpf"
                  name="cpf"
                  label={_('label/patient_form/cpf', null, s => `${s}:`)}
                  value={get(formData, 'cpf', '')}
                  onChange={this.handleFormChange}
                  error={includes(errors, 'cpf') ? _('error/cpf', 'global') : ''}
                />

              </div>
            </div>
          </div>
        </div>
        <div className="modalFooter">
          <Button
            disabled={isSavingPatient}
            text={_('action/cancel', 'global')}
            onClick={this.handleClose}
          />

          <Button
            isLoading={isSavingPatient}
            className="primary normal-spinner"
            text={_('action/save', 'global')}
            onClick={this.onCreatePatient}
          />
        </div>
      </Modal>
    );
  }

  handleFormChange = (data: FormElementData) => {
    const { name, value } = data;
    const { errors } = this.state;
    const formData: any = {
      ...this.state.formData,
      [name]: value,
    };

    let newErrors = errors;
    if (name === 'name') {
      newErrors = value !== '' ? without(errors, name) : concat(errors, name);
    } else if (name === 'email' && typeof value === 'string') {
      // An e-mail is only checked once you leave the field (or on Salvar), not at the first letter typed;
      // typing a valid one clears the error
      newErrors = isValidEmail(value) ? without(errors, name) : errors;
    } else if (name === 'mobilePhone' && String(value).length >= 10) {
      newErrors = isValidBrPhone.run(value, { allowEmpty: true }) ? without(errors, name) : concat(errors, name);
    } else if (name === 'cpf') {
      newErrors = isValidCpf.run(value, { allowEmpty: true }) ? without(errors, name) : concat(errors, name);
    } else if (name === 'birthdate' && String(value).length >= 10) {
      newErrors = isValidBrDate.run(value, { allowEmpty: true }) ? without(errors, name) : concat(errors, name);
    }

    this.setState({
      formData,
      errors: uniq(newErrors),
    });
  };

  handleEmailBlur = ({ value }: FormElementData) => {
    if (!isValidEmail(value)) {
      this.setState({ errors: uniq(concat(this.state.errors, 'email')) });
    }
  };

  onCreatePatient = () => {
    if (this.props.isSavingPatient) {
      return;
    }

    const errors = [];
    if (!this.state.formData.name) {
      errors.push('name');
    }

    if (!isValidEmail(this.state.formData.email)) {
      errors.push('email');
    }

    if (!isValidBrPhone.run(this.state.formData.mobilePhone, { allowEmpty: true })) {
      errors.push('mobilePhone');
    }

    if (!isValidCpf.run(this.state.formData.cpf, { allowEmpty: true, isSubmit: true })) {
      errors.push('cpf');
    }

    if (!isValidBrDate.run(this.state.formData.birthdate, { allowEmpty: true })) {
      errors.push('birthdate');
    }

    if (this.state.errors.length || errors.length) {
      this.setState({ errors: uniq(concat(this.state.errors, errors)) });
      return;
    }

    this.props.onCreatePatient(this.state.formData);
  };

  checkSpecialCharEnd = (text: string) => (
    endsWith(text, '/') || endsWith(text, '(') ||
    endsWith(text, ')') || endsWith(text, '.') ||
    endsWith(text, '-') || endsWith(text, ' ')
  );

  handleSpecialCharDelete = (e: Object) => {
    let endsWithSpecialChar = this.checkSpecialCharEnd(e.target.value);
    if (e.keyCode === 8) {
      while (endsWithSpecialChar) {
        e.target.value = e.target.value.slice(0, -1);
        endsWithSpecialChar = this.checkSpecialCharEnd(e.target.value);
      }
    }
  };

  handleClose = () => {
    if (this.props.previousModal === 'PatientSelectModal') {
      this.props.setModal('PatientSelectModal');
    } else {
      this.props.setModal(null);
    }
  };
}
